import * as yup from "yup";

const REQUIRED_FIELD_MESSAGE = "Campo obrigatório";
const INVALID_PHONE_MESSAGE = "Número de telefone inválido";
const INVALID_NUIT_MESSAGE = "O NUIT deve ter 9 digitos";
const INVALID_PASSWORD_MESSAGE = "A senha deve conter letras e digitos apenas";
const NOT_A_NUMBER_MESSAGE = "Valor deve ser numérico";
const INVALID_MIN_NUMBER_MESSAGE = "Número não pode ser menor que zero";
const INVALID_SCORE_MESSAGE =
  "Pontuação Inválida. Insira um número entre 0 e 5";
const INVALID_BUSINESS_PLAN_SCORE_MESSAGE =
  "Pontuação Inválida. Insira um número entre 0 e 20";

const isRequiredString = yup
  .string()
  .typeError(REQUIRED_FIELD_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);
const isRequiredNumber = yup.number().required(REQUIRED_FIELD_MESSAGE);
const isNumber = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .nullable();
const isMonetary = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .nullable();
const isNullablePositiveInteger = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .integer("Número deve ser inteiro")
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .nullable();
const isValidPhoneNumber = yup.string().matches(/^8(2|3|4|5|6|7)[0-9]{7}$/, {
  message: INVALID_PHONE_MESSAGE,
  excludeEmptyString: true,
});
const isValidNuit = yup
  .string()
  .matches(/^[0-9]{9}$/, {
    message: INVALID_NUIT_MESSAGE,
    excludeEmptyString: true,
  })
  .nullable();
const isValidEmail = yup.string().email("Email inválido").nullable();
const isValidDate = yup
  .string()
  .matches(
    /^(0[1-9]|1\d|2\d|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/,
    "Data inválida. Use o formato dd/mm/aaaa"
  );

const isValidFundingRequest = yup
  .number()
  .max(1500000, "Montante não pode ser superior a 1.500.000 MZN")
  .nullable();

const isRequiredPositiveInteger = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .integer("Número deve ser inteiro")
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);

const isRequiredMonetaryValue = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .required(REQUIRED_FIELD_MESSAGE);

const isRequiredFundingAmount = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(NOT_A_NUMBER_MESSAGE)
  .min(0, INVALID_MIN_NUMBER_MESSAGE)
  .max(1500000, "Montante não pode ser superior a 1.500.000 MZN")
  .required(REQUIRED_FIELD_MESSAGE);

const isRequiredBoolean = yup
  .boolean()
  .transform((value, originalValue) => {
    if (typeof originalValue === "string") {
      if (originalValue === "true") {
        return true;
      }
      if (originalValue === "false") {
        return false;
      }
    }
    if (originalValue === undefined || originalValue === null) {
      return undefined;
    }
    return value;
  })
  .required(REQUIRED_FIELD_MESSAGE);

const isValidScore = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(INVALID_SCORE_MESSAGE)
  .integer(INVALID_SCORE_MESSAGE)
  .min(0, INVALID_SCORE_MESSAGE)
  .max(5, INVALID_SCORE_MESSAGE);

const isValidBusinessPlanScore = yup
  .number()
  .transform((v, o) => (o === "" ? null : v))
  .typeError(INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .integer(INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .min(0, INVALID_BUSINESS_PLAN_SCORE_MESSAGE)
  .max(20, INVALID_BUSINESS_PLAN_SCORE_MESSAGE);

const roleRules = yup.object({
  roleCode: yup.string().required(REQUIRED_FIELD_MESSAGE),
});

const passwordRules = yup.object({
  password: yup
    .string()
    .transform((x) => (x === "" ? undefined : x))
    .required(REQUIRED_FIELD_MESSAGE)
    .min(6, "A senha deve ter no mínimo 6 caracteres")
    .max(20, "A senha deve ter no máximo 20 caracteres")
    .matches(
      /^(?=.*\d)[^#&!~<>?=+*()-+=\/?@{}_$%]+$/,
      INVALID_PASSWORD_MESSAGE
    ),
  passwordConfirm: yup
    .string()
    .transform((x) => (x === "" ? undefined : x))
    .required(REQUIRED_FIELD_MESSAGE)
    .oneOf([yup.ref("password")], "As senhas devem ser iguais"),
});

const userSchema = yup.object({
  firstName: isRequiredString,
  lastName: isRequiredString,
  username: isRequiredString.concat(isValidPhoneNumber),
  email: isValidEmail,
});

const selfKnowledgeQuestionnaireSchema = yup.object({
  issue1: isRequiredString,
  issue2: isRequiredString,
  issue3: isRequiredString,
  issue4: isRequiredString,
  issue5: isRequiredString,
  issue6: isRequiredString,
  issue7: isRequiredString,
  issue8: isRequiredString,
  issue9: isRequiredString,
  issue10: isRequiredString,
  issue11: isRequiredString,
  issue12: isRequiredString,
  issue13: isRequiredString,
  issue14: isRequiredString,
  issue15: isRequiredString,
  issue16: isRequiredString,
  issue17: isRequiredString,
  issue18: isRequiredString,
  issue19: isRequiredString,
  issue20: isRequiredString,
  issue21: isRequiredString,
  issue22: isRequiredString,
  issue23: isRequiredString,
  issue24: isRequiredString,
  issue25: isRequiredString,
});

const basicCandidatureSchema = yup.object({
  dataUseConsent: isRequiredString,
  name: isRequiredString,
  ownsABusiness: isRequiredString,
  email: isValidEmail,
  birthDate: isRequiredString.concat(isValidDate),
  gender: isRequiredString,
  totalWorkers: isNullablePositiveInteger,
  femaleWorkers: isNullablePositiveInteger,
  maleWorkers: isNullablePositiveInteger,
  femaleWorkers18To35: isNullablePositiveInteger,
  maleWorkers18To35: isNullablePositiveInteger,
  maxSalary: isMonetary,
  minSalary: isMonetary,
  projectedMaxSalary: isMonetary,
  projectedMinSalary: isMonetary,
  estimatedCurrentClients: isNullablePositiveInteger,
  projectedClientsYear1: isNullablePositiveInteger,
  projectedClientsYear2: isNullablePositiveInteger,
  projectedClientsYear3: isNullablePositiveInteger,
  lastYearTotalSales: isMonetary,
});

const submitApplicationSchema = yup.object({
  identificationDocTypeCode: isRequiredString,
  otherIdentificationDocType: yup
    .string()
    .when("identificationDocTypeCode", {
      is: "0",
      then: yup.string().required("Especifique o tipo"),
    })
    .nullable(true),
  identificationDocNumber: isRequiredString,
  phoneNumber: isRequiredString.concat(isValidPhoneNumber),
  provinceId: isRequiredString,
  districtId: isRequiredString,
  address: isRequiredString,
  academicLevelCode: isRequiredString,
  speaksAndUnderstandsPortuguese: isRequiredString,
  writesInPortuguese: isRequiredString,
  businessName: isRequiredString,
  legalFormCode: isRequiredString,
  hasLicense: isRequiredString,
  usesAccounting: isRequiredString,
  businessManagerCode: isRequiredString,
  totalWorkers: isRequiredString,
  femaleWorkers: isRequiredString,
  maleWorkers: isRequiredString,
  femaleWorkers18To35: isRequiredString,
  maleWorkers18To35: isRequiredString,
  minSalary: isRequiredString,
  maxSalary: isRequiredString,
  salaryEvolution: isRequiredString,
  mainClients: isRequiredString,
  estimatedCurrentClients: isRequiredString,
  projectedClientsYear1: isRequiredString,
  projectedClientsYear2: isRequiredString,
  projectedClientsYear3: isRequiredString,
  lastYearTotalSales: isRequiredString,
  ecommercePlatformCode: isRequiredString,
  // orderSourceCode: isRequiredString,
  knowsPossibleEnvironmentalEffects: isRequiredString,
  knowsHealthAndSafetyAtWork: isRequiredString,
  hasReceivedRelevantBusinessPerformanceTraining: isRequiredString,
  confirmsAvailabilityForTraining: isRequiredString,
  hasSpecialNeedsForTraining: isRequiredString,
  isAvailableForTrainingOutsideOfResidence: isRequiredString,
});

const basicBusinessPlanSchema = yup.object({
  // Section 1 - Data Sheet fields
  nur: isRequiredString,
  provinceId: isRequiredNumber,
  districtId: isRequiredNumber,
  administrativePost: isRequiredString,
  locality: isRequiredString,
  neighborhood: isRequiredString,
  addressTypeCode: isRequiredNumber,
  // Financial fields
  neededCapital: isMonetary,
  ownFunds: isMonetary,
  requestedFunds: isMonetary.concat(isValidFundingRequest),
  otherFunds: isMonetary,
  workers: yup.object({
    currentMales: isNullablePositiveInteger,
    currentMalesAged18To35: isNullablePositiveInteger,
    currentFemales: isNullablePositiveInteger,
    currentFemalesAged18To35: isNullablePositiveInteger,
    males2025: isNullablePositiveInteger,
    males2025Aged18To35: isNullablePositiveInteger,
    females2025: isNullablePositiveInteger,
    females2025Aged18To35: isNullablePositiveInteger,
    males2026: isNullablePositiveInteger,
    males2026Aged18To35: isNullablePositiveInteger,
    females2026: isNullablePositiveInteger,
    females2026Aged18To35: isNullablePositiveInteger,
    males2027: isNullablePositiveInteger,
    males2027Aged18To35: isNullablePositiveInteger,
    females2027: isNullablePositiveInteger,
    females2027Aged18To35: isNullablePositiveInteger,
  }),
  investmentPlan: yup.object({
    installations: isMonetary,
    adaptationOrRehabilitation: isMonetary,
    machineryAndTools: isMonetary,
    furniture: isMonetary,
    vehicles: isMonetary,
    computersAndDevices: isMonetary,
    motorcycle: isMonetary,
    householdAppliances: isMonetary,
    rawMaterialOrMerchandise: isMonetary,
    licensesAndFees: isMonetary,
    marketing: isMonetary,
    rent: isMonetary,
    energy: isMonetary,
    water: isMonetary,
    internet: isMonetary,
    officeSupplies: isMonetary,
    contingencies: isMonetary,
  }),
  salesPlan: yup.object({
    productService1Price2025: isMonetary,
    productService1Qty2025: isMonetary,
    productService1Price2026: isMonetary,
    productService1Qty2026: isMonetary,
    productService1Price2027: isMonetary,
    productService1Qty2027: isMonetary,
    productService2Price2025: isMonetary,
    productService2Qty2025: isMonetary,
    productService2Price2026: isMonetary,
    productService2Qty2026: isMonetary,
    productService2Price2027: isMonetary,
    productService2Qty2027: isMonetary,
    productService3Price2025: isMonetary,
    productService3Qty2025: isMonetary,
    productService3Price2026: isMonetary,
    productService3Qty2026: isMonetary,
    productService3Price2027: isMonetary,
    productService3Qty2027: isMonetary,
    otherProductServicePrice2025: isMonetary,
    otherProductServiceQty2025: isMonetary,
    otherProductServicePrice2026: isMonetary,
    otherProductServiceQty2026: isMonetary,
    otherProductServicePrice2027: isMonetary,
    otherProductServiceQty2027: isMonetary,
  }),

  provisionalPlan: yup.object({
    salesVolume2025: isMonetary,
    costs2025: isMonetary,
    otherCostsNotCovered2025: isMonetary,
    grossProfit2025: isMonetary,
    salesVolume2026: isMonetary,
    costs2026: isMonetary,
    otherCostsNotCovered2026: isMonetary,
    grossProfit2026: isMonetary,
    salesVolume2027: isMonetary,
    costs2027: isMonetary,
    otherCostsNotCovered2027: isMonetary,
    grossProfit2027: isMonetary,
  }),
});

const submitBusinessPlanSchema = basicBusinessPlanSchema.concat(
  yup.object({
    // Section 2 - Existing Business Profile
    ownerEducationLevel: isRequiredString,
    ownerExperienceYears: isRequiredPositiveInteger,
    ownerBusinessExperience: isRequiredString,
    partner1EducationLevel: isRequiredString,
    partner1ExperienceYears: isRequiredPositiveInteger,
    partner1BusinessExperience: isRequiredString,
    // partner2EducationLevel: isRequiredString,
    // partner2ExperienceYears: isRequiredPositiveInteger,
    // partner2BusinessExperience: isRequiredString,
    hasPermitOrLicenses: isRequiredBoolean,
    hasEnvironmentalLicense: isRequiredBoolean,
    legalFormOfBusinessCode: isRequiredNumber,
    productServiceDescription: isRequiredString,
    competitorDifference: isRequiredString,

    // Section 3 - Market Analysis
    approximateNumberOfCustomers: isRequiredPositiveInteger,
    customerDescription: isRequiredString,
    productServiceImprovements: isRequiredString,
    marketingStrategiesAndChannels: isRequiredString,

    // Section 4 - Workers
    workers: yup
      .object({
        totalCollaborators: isRequiredPositiveInteger,
        currentMalesAged18To35: isRequiredPositiveInteger,
        currentFemalesAged18To35: isRequiredPositiveInteger,
        currentMalesAbove35: isRequiredPositiveInteger,
        currentFemalesAbove35: isRequiredPositiveInteger,
        males2025: isRequiredPositiveInteger,
        males2025Aged18To35: isRequiredPositiveInteger,
        females2025: isRequiredPositiveInteger,
        females2025Aged18To35: isRequiredPositiveInteger,
        males2026: isRequiredPositiveInteger,
        males2026Aged18To35: isRequiredPositiveInteger,
        females2026: isRequiredPositiveInteger,
        females2026Aged18To35: isRequiredPositiveInteger,
        males2027: isRequiredPositiveInteger,
        males2027Aged18To35: isRequiredPositiveInteger,
        females2027: isRequiredPositiveInteger,
        females2027Aged18To35: isRequiredPositiveInteger,
      })
      .required(REQUIRED_FIELD_MESSAGE),

    // Section 5 - Financial Plan
    investmentPlan: yup
      .object({
        adaptationOrRehabilitation: isRequiredMonetaryValue,
        machineryAndTools: isRequiredMonetaryValue,
        furniture: isRequiredMonetaryValue,
        vehicles: isRequiredMonetaryValue,
        computersAndDevices: isRequiredMonetaryValue,
        motorcycle: isRequiredMonetaryValue,
        householdAppliances: isRequiredMonetaryValue,
        rawMaterialOrMerchandise: isRequiredMonetaryValue,
        licensesAndFees: isRequiredMonetaryValue,
        marketing: isRequiredMonetaryValue,
        rent: isRequiredMonetaryValue,
        energy: isRequiredMonetaryValue,
        water: isRequiredMonetaryValue,
        internet: isRequiredMonetaryValue,
        officeSupplies: isRequiredMonetaryValue,
        contingencies: isRequiredMonetaryValue,
      })
      .required(REQUIRED_FIELD_MESSAGE),
    salesPlan: yup
      .object({
        productService1Desc: isRequiredString,
        productService1Price2025: isRequiredMonetaryValue,
        productService1Qty2025: isRequiredPositiveInteger,
        productService1Price2026: isRequiredMonetaryValue,
        productService1Qty2026: isRequiredPositiveInteger,
        productService1Price2027: isRequiredMonetaryValue,
        productService1Qty2027: isRequiredPositiveInteger,
        productService2Desc: yup.string().nullable(),
        productService2Price2025: isMonetary,
        productService2Qty2025: isMonetary,
        productService2Price2026: isMonetary,
        productService2Qty2026: isMonetary,
        productService2Price2027: isMonetary,
        productService2Qty2027: isMonetary,
        productService3Desc: yup.string().nullable(),
        productService3Price2025: isMonetary,
        productService3Qty2025: isMonetary,
        productService3Price2026: isMonetary,
        productService3Qty2026: isMonetary,
        productService3Price2027: isMonetary,
        productService3Qty2027: isMonetary,
        otherProductServiceDesc: yup.string().nullable(),
        otherProductServicePrice2025: isMonetary,
        otherProductServiceQty2025: isMonetary,
        otherProductServicePrice2026: isMonetary,
        otherProductServiceQty2026: isMonetary,
        otherProductServicePrice2027: isMonetary,
        otherProductServiceQty2027: isMonetary,
      })
      .required(REQUIRED_FIELD_MESSAGE),
    provisionalPlan: yup
      .object({
        salesVolume2025: isRequiredMonetaryValue,
        costs2025: isRequiredMonetaryValue,
        otherCostsNotCovered2025: isRequiredMonetaryValue,
        salesVolume2026: isRequiredMonetaryValue,
        costs2026: isRequiredMonetaryValue,
        otherCostsNotCovered2026: isRequiredMonetaryValue,
        salesVolume2027: isRequiredMonetaryValue,
        costs2027: isRequiredMonetaryValue,
        otherCostsNotCovered2027: isRequiredMonetaryValue,
      })
      .required(REQUIRED_FIELD_MESSAGE),
    conectaNegociosFinancing: isRequiredFundingAmount,

    // Section 6 - Environmental and Social Safeguards
    environmentalEffectDec: isRequiredString,
    mitigationMeasures: isRequiredString,
    doesInvolveTheUseOfLand: isRequiredBoolean,
    hasSomeFormalLandTitle: isRequiredBoolean,
    formalLandTitle: yup
      .string()
      .transform((value) => (value === "" ? null : value))
      .when("hasSomeFormalLandTitle", {
        is: true,
        then: isRequiredString,
        otherwise: yup.string().nullable(true),
      }),
    isTitleValid: isRequiredBoolean,
    doesInvolveRehabilitationOronstruction: isRequiredBoolean,
    hasValidLicence: isRequiredBoolean,
  })
);

const submitExistingBusinessProfileSchema = yup.object({
  legalFormOfBusinessCode: isRequiredString,
  businessProductOrService: isRequiredString,
  businessHistory: isRequiredString,
});

const accountRecoverySchema = yup.object({
  identificationDocNumber: isRequiredString,
  phoneNumber: isRequiredString.concat(isValidPhoneNumber),
});

const businessPlanScoreSchema = yup.object({
  bp001: isValidBusinessPlanScore,
  bp002: isValidBusinessPlanScore,
  bp003: isValidBusinessPlanScore,
  bp004: isValidBusinessPlanScore,
  bp005: isValidBusinessPlanScore,
  bp006: isValidBusinessPlanScore,
  bp007: isValidBusinessPlanScore,
  bp008: isValidBusinessPlanScore,
  bp009: isValidBusinessPlanScore,
  bp010: isValidBusinessPlanScore,
  bp011: isValidBusinessPlanScore,
  bp012: isValidBusinessPlanScore,
});

export {
  userSchema,
  roleRules,
  passwordRules,
  selfKnowledgeQuestionnaireSchema,
  basicCandidatureSchema,
  submitApplicationSchema,
  basicBusinessPlanSchema,
  submitBusinessPlanSchema,
  submitExistingBusinessProfileSchema,
  accountRecoverySchema,
  businessPlanScoreSchema,
};
